/**
 * 约束求解 —— 对应技术设计 §5.5「连接后器材要跟得上」。
 *
 * 【§22 红线 2 就落在这个文件里】
 * 「器材连接后必须保持约束同步，移动试管或集气瓶时导管不能断层、漂移或穿帮。」
 *
 * 做法：把「被拖动的器材」当作根，沿 active 关系做**广度优先传播**，
 * 逐条按 Relation.constraints 声明的类型把下游器材摆回该在的位置。
 * 每个器材只解一次（visited 集合），避免 A↔B 互相约束时来回抖。
 */

import type { AssemblyGraph } from "../eom/assembly-graph.js";
import { listRelations, updateObject } from "../eom/assembly-graph.js";
import type { LabObject } from "../eom/lab-object.js";
import { toWorld } from "../eom/lab-object.js";
import type { Constraint, Endpoint, Relation } from "../eom/relation.js";
import type { ConstraintKind } from "./action-grammar.js";

/** 夹持后试管口略向下（§13.4），单位：度。正值 = 顺时针 = 管口向下。 */
export const CLAMPED_TILT_DEG = 12;

function anchorLocal(object: LabObject, endpoint: Endpoint): { x: number; y: number } | null {
  if (endpoint.portId !== undefined) {
    return object.ports?.find((port) => port.id === endpoint.portId)?.anchor ?? null;
  }
  if (endpoint.slotId !== undefined) {
    return object.slots?.find((slot) => slot.id === endpoint.slotId)?.anchor ?? null;
  }
  return { x: 0, y: 0 };
}

function constraintKindOf(relation: Relation): ConstraintKind | null {
  const declared = relation.constraints?.find((c) => c.kind !== undefined) as Constraint | undefined;
  return (declared?.kind as ConstraintKind | undefined) ?? null;
}

/**
 * 让 `follower` 的 `followerEnd` 锚点贴到 `anchorWorld`，只平移不旋转。
 * 返回新对象（不就地改）。
 */
function translateSoAnchorMeets(
  follower: LabObject,
  followerEnd: Endpoint,
  anchorWorld: { x: number; y: number },
): LabObject {
  const local = anchorLocal(follower, followerEnd);
  if (!local) return follower;
  const current = toWorld(follower, local);
  return {
    ...follower,
    pose: {
      ...follower.pose,
      x: follower.pose.x + (anchorWorld.x - current.x),
      y: follower.pose.y + (anchorWorld.y - current.y),
    },
  };
}

/**
 * 解一条关系：已知 `driver`（已就位的一侧），把另一侧摆好。
 * 返回 null 表示这条关系不需要移动对方（如 anchorEnd 的导管只重绘中段）。
 */
function solveRelation(
  relation: Relation,
  driver: LabObject,
  follower: LabObject,
  driverEnd: Endpoint,
  followerEnd: Endpoint,
): LabObject | null {
  const kind = constraintKindOf(relation);
  const driverLocal = anchorLocal(driver, driverEnd);
  if (!driverLocal) return null;
  const anchorWorld = toWorld(driver, driverLocal);

  switch (kind) {
    case "followPort":
      return translateSoAnchorMeets(follower, followerEnd, anchorWorld);

    case "contain":
      // 「倒进去 / 放进去」：内容物被容器纳入，**容器才是锚**。
      // 位置不做联动 —— 药品倒进试管后，试剂瓶该回它原来的位置，
      // 而不是把试管拴住。早期按 followPort 处理，导致试管被药品拖住夹不进铁架台
      // （`bug/023-化学/002-bug.md` 卡在第 4 步的真实原因）。
      return null;

    case "poseOverride": {
      // 夹持：先按端口对齐，再强制校正姿态（§13.4 试管口略向下）
      const moved = translateSoAnchorMeets(follower, followerEnd, anchorWorld);
      return {
        ...moved,
        pose: { ...moved.pose, rotation: CLAMPED_TILT_DEG },
        state: { ...moved.state, isClamped: true, mouthDirection: "right_down" },
      };
    }

    case "anchorEnd":
      // 导管这类**两端各自锚定**的器材：本体不整体跟随，
      // 渲染层按两端世界坐标重绘中段路径（svg-layer.updateRelationPath）。
      return null;

    default:
      return null;
  }
}

/**
 * 约束是**有向**的：`followPort` / `poseOverride` / `contain` 都表示
 * 「`to` 侧跟随 `from` 侧」，不是双向绑定。
 *
 * 这条方向性不能省。反例：橡胶塞以 `to` 身份插在试管口上（试管口是 `from`），
 * 若约束双向，拖动橡胶塞就会把已经被铁架台夹住的试管一起拽走 —— 一个几克的塞子
 * 拖动整套装置，既不真实也让学生无法单独取下塞子。
 *
 * 所以传播只沿 from → to 一个方向。拖动 `to` 侧器材的处理在 drag-controller：
 * 拖得够远就断开，不够远就弹回原位。
 */
function propagationTarget(relation: Relation, driverId: string): { own: Endpoint; other: Endpoint } | null {
  if (relation.from.objectId !== driverId) return null;
  return { own: relation.from, other: relation.to };
}

/**
 * 从 `rootId` 出发传播约束，返回新图。
 *
 * `rootId` 是刚被移动的器材 —— 它的 pose 已经是新的了，本函数不动它，
 * 只负责让所有直接或间接连着它的器材跟上。
 */
export function solveConstraints(graph: AssemblyGraph, rootId: string): AssemblyGraph {
  const relations = listRelations(graph).filter((relation) => relation.state === "active");
  if (relations.length === 0) return graph;

  let next = graph;
  const visited = new Set<string>([rootId]);
  const queue: string[] = [rootId];

  while (queue.length > 0) {
    const currentId = queue.shift()!;
    const driver = next.nodes[currentId];
    if (!driver) continue;

    for (const relation of relations) {
      const ends = propagationTarget(relation, currentId);
      if (!ends) continue;

      const followerId = ends.other.objectId;
      if (visited.has(followerId)) continue;

      const follower = next.nodes[followerId];
      if (!follower) continue;

      const solved = solveRelation(relation, driver, follower, ends.own, ends.other);
      if (solved) {
        next = updateObject(next, followerId, { pose: solved.pose, state: solved.state });
      }

      visited.add(followerId);
      queue.push(followerId);
    }
  }

  return next;
}

/**
 * 拖动 `to` 侧器材时，哪些 active 关系是「可被拽断」的。
 *
 * 不含：
 * - `anchorEnd`（导管端）—— 导管本来就只锚一端，拖动它不该断开
 * - `contain`（倒进/放进容器）—— 容器是锚，拖动容器不该把里面的东西倒出来。
 *   否则试管里一有药品就再也拖不动了。
 */
export function breakableRelations(graph: AssemblyGraph, objectId: string): Relation[] {
  return listRelations(graph).filter((relation) => {
    if (relation.state !== "active") return false;
    if (relation.to.objectId !== objectId) return false;
    const kind = constraintKindOf(relation);
    return kind === "followPort" || kind === "poseOverride";
  });
}

/**
 * 导管这类两端锚定器材的中段路径：返回两端的世界坐标，渲染层据此重绘贝塞尔。
 * 端点没连上时返回该端在当前 pose 下的世界坐标，保证路径始终连续（不断层）。
 */
export function resolveTubeEnds(
  graph: AssemblyGraph,
  tubeId: string,
): { input: { x: number; y: number }; output: { x: number; y: number } } | null {
  const tube = graph.nodes[tubeId];
  if (!tube) return null;

  const endWorld = (portId: string): { x: number; y: number } => {
    const attached = listRelations(graph).find(
      (relation) =>
        relation.state === "active" &&
        ((relation.from.objectId === tubeId && relation.from.portId === portId) ||
          (relation.to.objectId === tubeId && relation.to.portId === portId)),
    );

    if (attached) {
      const other =
        attached.from.objectId === tubeId ? attached.to : attached.from;
      const otherObject = graph.nodes[other.objectId];
      if (otherObject) {
        const local = anchorLocal(otherObject, other);
        if (local) return toWorld(otherObject, local);
      }
    }

    const local = anchorLocal(tube, { objectId: tubeId, portId });
    return local ? toWorld(tube, local) : { x: tube.pose.x, y: tube.pose.y };
  };

  return { input: endWorld("input"), output: endWorld("output") };
}
